import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// Stored role values — NOT the display names. 'admin' is shown as "Super Admin",
// 'supply_manager' as "Supplier", 'manager' as the limited "Admin" tier; see
// ROLE_LABELS and the AdminRole comment in @fursahub/shared. Existing values never
// change meaning: this portal deploys from main while the production API deploys
// from prod, so both must read every stored value the same way.
// What each role may see and do is the permission matrix — config/permissions.ts
// (fallback copy) and GET /admin/permissions (the server's), read via
// usePermissions. Where a role lands after login is the first page it may open.
export type AdminRole =
  | 'admin'
  | 'supply_manager'
  | 'manager'
  | 'editor'
  | 'content_moderator'
  | 'creatives_manager'
  | 'marketing_manager'

/** What each stored role is called in the UI. */
export const ROLE_LABELS: Record<AdminRole, string> = {
  admin: 'Super Admin',
  supply_manager: 'Supplier',
  manager: 'Admin',
  editor: 'Editor',
  content_moderator: 'Content Moderator',
  creatives_manager: 'Creatives',
  marketing_manager: 'Marketing',
}

interface AdminInfo {
  _id: string
  email: string
  role: AdminRole
}

interface AuthState {
  accessToken: string | null
  refreshToken: string | null
  admin: AdminInfo | null
  setTokens: (accessToken: string, refreshToken: string, admin: AdminInfo) => void
  setAccessToken: (token: string) => void
  logout: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      admin: null,
      setTokens: (accessToken, refreshToken, admin) =>
        set({ accessToken, refreshToken, admin }),
      setAccessToken: (accessToken) => set({ accessToken }),
      logout: () => set({ accessToken: null, refreshToken: null, admin: null }),
    }),
    { name: 'fursahub-admin-auth' },
  ),
)
